( function () {

	/**
	 * Wire up a single tabs instance.
	 *
	 * @param {HTMLElement} wrapper - The .wp-block-gandaki-govt-tabs element.
	 */
	function initTabs( wrapper ) {
		const tablist = wrapper.querySelector( '[role="tablist"]' );
		if ( ! tablist ) return;

		const orientation = wrapper.dataset.orientation || 'horizontal';
		const animate     = wrapper.dataset.animate === '1';

		function getTabs()   { return Array.from( tablist.querySelectorAll( '[role="tab"]' ) ); }
		function getPanels() { return Array.from( wrapper.querySelectorAll( '[role="tabpanel"]' ) ); }

		function activateTab( tab ) {
			const tabs    = getTabs();
			const panels  = getPanels();
			const panelId = tab.getAttribute( 'aria-controls' );
			const target  = panelId ? wrapper.querySelector( '#' + panelId ) : null;

			// Deactivate all tabs.
			tabs.forEach( ( t ) => {
				t.setAttribute( 'aria-selected', 'false' );
				t.setAttribute( 'tabindex', '-1' );
				t.classList.remove( 'is-active' );
			} );

			// Hide all panels except the target.
			// Only add a transitionend listener to panels that are CURRENTLY VISIBLE —
			// adding a listener to a display:none panel risks a spurious transitionend
			// firing before is-visible is added via rAF, which would re-hide the target.
			panels.forEach( ( p ) => {
				if ( p === target ) return;

				const wasVisible = ! p.hidden && p.classList.contains( 'is-visible' );
				p.classList.remove( 'is-visible' );

				if ( animate && wasVisible ) {
					// Fade visible panel out, then hide it after the transition.
					p.addEventListener( 'transitionend', function handler() {
						p.hidden = true;
						p.removeEventListener( 'transitionend', handler );
					}, { once: true } );
				} else {
					// Panel was already hidden — no transition possible, hide immediately.
					p.hidden = true;
				}
			} );

			// Activate the selected tab.
			tab.setAttribute( 'aria-selected', 'true' );
			tab.setAttribute( 'tabindex', '0' );
			tab.classList.add( 'is-active' );

			// Show the target panel.
			if ( target ) {
				target.hidden = false;
				if ( animate ) {
					// rAF ensures the browser has committed display:block before the
					// opacity transition starts (transitions don't run on display:none).
					requestAnimationFrame( () => target.classList.add( 'is-visible' ) );
				} else {
					target.classList.add( 'is-visible' );
				}
			}
		}

		// Delegated click handler — one listener for the whole tablist.
		tablist.addEventListener( 'click', function ( e ) {
			const tab = e.target.closest( '[role="tab"]' );
			if ( ! tab ) return;
			activateTab( tab );
			tab.focus();
		} );

		// WAI-ARIA keyboard pattern for tabs.
		tablist.addEventListener( 'keydown', function ( e ) {
			const tabs = getTabs();
			const idx  = tabs.indexOf( document.activeElement );
			if ( idx === -1 ) return;

			const isHorizontal = orientation === 'horizontal';
			let next = idx;

			if ( ( isHorizontal && e.key === 'ArrowRight' ) || ( ! isHorizontal && e.key === 'ArrowDown' ) ) {
				next = ( idx + 1 ) % tabs.length;
			} else if ( ( isHorizontal && e.key === 'ArrowLeft' ) || ( ! isHorizontal && e.key === 'ArrowUp' ) ) {
				next = ( idx - 1 + tabs.length ) % tabs.length;
			} else if ( e.key === 'Home' ) {
				next = 0;
			} else if ( e.key === 'End' ) {
				next = tabs.length - 1;
			} else {
				return;
			}

			e.preventDefault();
			tabs[ next ].focus();
			activateTab( tabs[ next ] );
		} );

		// Bootstrap: PHP render.php already set `hidden` on inactive panels via
		// preg_replace injection. JS now takes ownership of visibility state.
		// IntersectionObserver note: `hidden` → display:none means elements inside
		// inactive tabs (e.g. facebook-page-embed lazy iframes) will not intersect
		// with the viewport until their tab is activated — correct behavior.
		const tabs          = getTabs();
		const panels        = getPanels();
		const activeTab     = tabs.find( ( t ) => t.getAttribute( 'aria-selected' ) === 'true' ) ?? tabs[ 0 ];
		const activePanelId = activeTab ? activeTab.getAttribute( 'aria-controls' ) : null;

		panels.forEach( ( p ) => {
			if ( p.id !== activePanelId ) {
				p.hidden = true;
				p.classList.remove( 'is-visible' );
			} else {
				p.hidden = false;
				if ( animate ) {
					requestAnimationFrame( () => p.classList.add( 'is-visible' ) );
				} else {
					p.classList.add( 'is-visible' );
				}
			}
		} );
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		document
			.querySelectorAll( '.wp-block-gandaki-govt-tabs' )
			.forEach( initTabs );
	} );

} )();
